import { FileText, FileCode, FileImage, File, Clock } from 'lucide-react';
import { useDocsStore } from '../../stores/docsStore';
import { Spinner } from '../common/Spinner';
import { EmptyState } from '../common/EmptyState';
import type { RecentFile } from '../../types/docs';

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  return `${(bytes / 1024).toFixed(1)} KB`;
}

function formatTime(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function shortenDir(dir: string): string {
  return dir
    .replace('/opt/union-threads/', '')
    .replace('/var/lib/docker/volumes/ut-secondbrain_workspace-data/_data/', 'SB:');
}

const CODE_EXTENSIONS = new Set(['ts', 'tsx', 'js', 'jsx', 'py', 'sh', 'bash', 'css', 'scss', 'html', 'sql']);
const IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'ico']);

function getFileIcon(name: string, viewable: boolean) {
  if (viewable) return <FileText className="h-3.5 w-3.5 flex-shrink-0 mt-0.5 text-text-muted" />;
  const ext = name.includes('.') ? name.split('.').pop()!.toLowerCase() : '';
  if (CODE_EXTENSIONS.has(ext)) return <FileCode className="h-3.5 w-3.5 flex-shrink-0 mt-0.5 text-text-muted/50" />;
  if (IMAGE_EXTENSIONS.has(ext)) return <FileImage className="h-3.5 w-3.5 flex-shrink-0 mt-0.5 text-text-muted/50" />;
  return <File className="h-3.5 w-3.5 flex-shrink-0 mt-0.5 text-text-muted/50" />;
}

/** Group files by their parent directory */
function groupByDirectory(files: RecentFile[]): Map<string, RecentFile[]> {
  const groups = new Map<string, RecentFile[]>();
  for (const file of files) {
    const key = shortenDir(file.directory);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(file);
  }
  return groups;
}

export function RecentFilesList() {
  const { recentFiles, recentLoading, selectFile, viewMode } = useDocsStore();

  if (recentLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner size="sm" />
      </div>
    );
  }

  if (recentFiles.length === 0) {
    return (
      <div className="p-4">
        <EmptyState
          icon={<Clock />}
          title="No files modified today"
          description="No files have been modified today."
        />
      </div>
    );
  }

  const grouped = groupByDirectory(recentFiles);

  return (
    <div className="py-1">
      <div className="px-3 py-1.5">
        <span className="text-[10px] text-text-muted">
          {recentFiles.length} file{recentFiles.length !== 1 ? 's' : ''} modified today
        </span>
      </div>
      {Array.from(grouped.entries()).map(([dir, files]) => (
        <div key={dir} className="mb-2">
          <div className="px-3 py-1 text-[10px] font-medium text-text-muted uppercase tracking-wide truncate" title={dir}>
            {dir}
          </div>
          {files.map(file => {
            const isViewable = file.viewable !== false;

            if (!isViewable) {
              return (
                <div
                  key={file.path}
                  className="w-full flex items-start gap-2 px-3 py-1.5 text-left opacity-50 cursor-default"
                  title="Preview not available"
                >
                  {getFileIcon(file.name, false)}
                  <div className="min-w-0 flex-1">
                    <div className="text-xs text-text-muted truncate">{file.name}</div>
                    <div className="flex items-center gap-2 text-[10px] text-text-muted">
                      <span>{formatTime(file.lastModified)}</span>
                      <span>{formatSize(file.size)}</span>
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <button
                key={file.path}
                onClick={() => {
                  if (viewMode === 'today') {
                    useDocsStore.getState().navigateToPath(file.path);
                  } else {
                    selectFile(file.path);
                  }
                }}
                className="w-full flex items-start gap-2 px-3 py-1.5 text-left hover:bg-bg-tertiary rounded transition-colors"
              >
                {getFileIcon(file.name, true)}
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-text-primary truncate">{file.name}</div>
                  <div className="flex items-center gap-2 text-[10px] text-text-muted">
                    <span>{formatTime(file.lastModified)}</span>
                    <span>{formatSize(file.size)}</span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

export default RecentFilesList;
